import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const SystemHealthPanel = () => {
  const [healthData, setHealthData] = useState({
    database: { status: 'healthy', latency: 45, uptime: '99.9%' },
    storage: { status: 'healthy', usage: '45%', available: '2.1 TB' },
    auth: { status: 'healthy', activeUsers: 1247, sessionsToday: 3456 },
    api: { status: 'healthy', requestsToday: 12845, avgResponseTime: 120 }
  });
  
  const [systemStats, setSystemStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalCollectibles: 0,
    storageUsed: 0
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading system health data
    const loadHealthData = async () => {
      setLoading(true);
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock system stats - in real implementation, fetch from your analytics service
      setSystemStats({
        totalUsers: 1247,
        activeUsers: 89,
        totalCollectibles: 5689,
        storageUsed: 45.2
      });
      
      setLoading(false);
    };

    loadHealthData();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'text-green-500';
      case 'warning':
        return 'text-yellow-500';
      case 'error':
        return 'text-red-500';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-500/10';
      case 'warning':
        return 'bg-yellow-500/10';
      case 'error':
        return 'bg-red-500/10';
      default:
        return 'bg-muted/10';
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="flex items-center space-x-3 text-muted-foreground">
          <div className="w-5 h-5 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></div>
          <span>Loading system health...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-foreground">System Health Monitor</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Real-time system performance and health metrics
        </p>
      </div>
      {/* Overall Status */}
      <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
            <Icon name="CheckCircle" size={16} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-green-700 dark:text-green-300">
              All Systems Operational
            </h3>
            <p className="text-sm text-green-600 dark:text-green-400">
              All services are running normally
            </p>
          </div>
        </div>
      </div>
      {/* System Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Users" size={20} className="text-primary" />
            <span className="text-xs text-muted-foreground">Total</span>
          </div>
          <div className="text-2xl font-bold text-foreground">{systemStats?.totalUsers?.toLocaleString()}</div>
          <div className="text-sm text-muted-foreground">Registered Users</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Activity" size={20} className="text-green-500" />
            <span className="text-xs text-muted-foreground">Now</span>
          </div>
          <div className="text-2xl font-bold text-foreground">{systemStats?.activeUsers}</div>
          <div className="text-sm text-muted-foreground">Active Users</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Package" size={20} className="text-blue-500" />
            <span className="text-xs text-muted-foreground">Items</span>
          </div>
          <div className="text-2xl font-bold text-foreground">{systemStats?.totalCollectibles?.toLocaleString()}</div>
          <div className="text-sm text-muted-foreground">Total Collectibles</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="HardDrive" size={20} className="text-purple-500" />
            <span className="text-xs text-muted-foreground">Used</span>
          </div>
          <div className="text-2xl font-bold text-foreground">{systemStats?.storageUsed}%</div>
          <div className="text-sm text-muted-foreground">Storage Usage</div>
        </div>
      </div>
      {/* Service Health */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">Service Status</h3>
        
        <div className="grid gap-4">
          {/* Database */}
          <div className="flex items-center justify-between p-4 bg-card border border-border rounded-lg">
            <div className="flex items-center space-x-4">
              <div className={`w-3 h-3 rounded-full ${healthData?.database?.status === 'healthy' ? 'bg-green-500' : 'bg-red-500'} animate-pulse`}></div>
              <div>
                <h4 className="font-medium text-foreground">Database</h4>
                <p className="text-sm text-muted-foreground">Supabase PostgreSQL</p>
              </div>
            </div>
            <div className="text-right">
              <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBg(healthData?.database?.status)} ${getStatusColor(healthData?.database?.status)}`}>
                {healthData?.database?.status}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {healthData?.database?.latency}ms avg • {healthData?.database?.uptime} uptime
              </div>
            </div>
          </div>

          {/* Storage */}
          <div className="flex items-center justify-between p-4 bg-card border border-border rounded-lg">
            <div className="flex items-center space-x-4">
              <div className={`w-3 h-3 rounded-full ${healthData?.storage?.status === 'healthy' ? 'bg-green-500' : 'bg-red-500'} animate-pulse`}></div>
              <div>
                <h4 className="font-medium text-foreground">Storage</h4>
                <p className="text-sm text-muted-foreground">File & Image Storage</p>
              </div>
            </div>
            <div className="text-right">
              <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBg(healthData?.storage?.status)} ${getStatusColor(healthData?.storage?.status)}`}>
                {healthData?.storage?.status}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {healthData?.storage?.usage} used • {healthData?.storage?.available} available
              </div>
            </div>
          </div>

          {/* Authentication */}
          <div className="flex items-center justify-between p-4 bg-card border border-border rounded-lg">
            <div className="flex items-center space-x-4">
              <div className={`w-3 h-3 rounded-full ${healthData?.auth?.status === 'healthy' ? 'bg-green-500' : 'bg-red-500'} animate-pulse`}></div>
              <div>
                <h4 className="font-medium text-foreground">Authentication</h4>
                <p className="text-sm text-muted-foreground">User Authentication Service</p>
              </div>
            </div>
            <div className="text-right">
              <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBg(healthData?.auth?.status)} ${getStatusColor(healthData?.auth?.status)}`}>
                {healthData?.auth?.status}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {healthData?.auth?.activeUsers} active • {healthData?.auth?.sessionsToday} sessions today
              </div>
            </div>
          </div>

          {/* API */}
          <div className="flex items-center justify-between p-4 bg-card border border-border rounded-lg">
            <div className="flex items-center space-x-4">
              <div className={`w-3 h-3 rounded-full ${healthData?.api?.status === 'healthy' ? 'bg-green-500' : 'bg-red-500'} animate-pulse`}></div>
              <div>
                <h4 className="font-medium text-foreground">API Gateway</h4>
                <p className="text-sm text-muted-foreground">REST API & Real-time</p>
              </div>
            </div>
            <div className="text-right">
              <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBg(healthData?.api?.status)} ${getStatusColor(healthData?.api?.status)}`}>
                {healthData?.api?.status}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {healthData?.api?.requestsToday?.toLocaleString()} requests • {healthData?.api?.avgResponseTime}ms avg
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Actions */}
      <div className="mt-8 flex items-center justify-between pt-6 border-t border-border">
        <div className="text-sm text-muted-foreground">
          Last updated: {new Date()?.toLocaleString()}
        </div>
        <button
          onClick={() => window.location?.reload()}
          className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Icon name="RefreshCw" size={16} />
          <span>Refresh</span>
        </button>
      </div>
    </div>
  );
};

export default SystemHealthPanel;